import {
  Audio,
  Blank,
  BlockBackground,
  BlockTextVisibility,
  Blockquote,
  BulletList,
  CadmusEditorWrapper,
  Code,
  Collab,
  Document,
  Emoji,
  EmptyMediaNode,
  FootnoteStatic,
  Highlight,
  Hyperlink,
  Image,
  Indentation,
  List,
  ListItem,
  MathServerExtension,
  OrderedList,
  PasteChip,
  Subscript,
  Superscript,
  Table,
  TaskItem,
  TaskList,
  Telemetry,
  TextColor,
  Thread,
  TrailingNode,
  Video,
  editorWordCount,
  getLanguageDisplay,
  getLanguageMode,
  getText,
  listFootnotes,
  textWordCount
} from "./chunk-WA3WIKHK.mjs";

// src/extensions/cadmus-kit/cadmus-kit-server.ts
import { Extension } from "@tiptap/core";
import { Bold } from "@tiptap/extension-bold";
import { HardBreak } from "@tiptap/extension-hard-break";
import { Heading } from "@tiptap/extension-heading";
import { Italic } from "@tiptap/extension-italic";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Strike } from "@tiptap/extension-strike";
import { Text } from "@tiptap/extension-text";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import { Underline } from "@tiptap/extension-underline";
import { Plugin } from "@tiptap/pm/state";

// src/extensions/code-block-server/code-block-server.ts
import { Node } from "@tiptap/core";

// src/extensions/code-block-server/makeCodeBlockDomSpec.ts
import { mergeAttributes } from "@tiptap/core";
import CodeMirror from "codemirror";
import "codemirror/addon/runmode/runmode-standalone";
function makeCodeBlockDomSpec(text, language, theme = "cadmus-light" /* Light */, showLineNumbers = true, showLanguage = true) {
  const gutterDiv = [
    "div",
    { class: "CodeMirror-gutters", style: "height: 122px; left: 0px" },
    [
      "div",
      {
        class: "CodeMirror-gutter CodeMirror-linenumbers",
        style: `width: ${showLineNumbers ? "29px" : "0px"}`
      }
    ]
  ];
  const tokenisedText = tokeniseToDomSpec(text, language, showLineNumbers);
  const codeMirrorSizer = [
    "div",
    {
      class: "CodeMirror-sizer",
      style: codeMirrorSizerStyleTag
    },
    [
      "div",
      // These styles are added to emulate the wrapper that `CodeMirror`
      // adds to `div.CodeMirror-lines`. The conditional `margin-left` is added
      // to accomodate for the show/hide line numbers option.
      {
        style: `position: relative; top: 0px; margin-left: ${showLineNumbers ? "0px" : "-29px"}; margin-right: -50px; margin-bottom: -50px; padding-bottom:50px`
      },
      [
        "div",
        { class: "CodeMirror-lines", role: "presentation" },
        [
          "div",
          { class: "CodeMirror-code", role: "presentation" },
          ...tokenisedText
        ]
      ]
    ]
  ];
  const codeMirrorDivClasses = showLanguage ? `CodeMirror cm-s-${theme} CodeMirror-wrap` : `CodeMirror cm-s-${theme} language-display-off CodeMirror-wrap`;
  const codeBlockDiv = [
    "div",
    mergeAttributes({
      class: codeMirrorDivClasses,
      translate: "no",
      draggable: "false",
      contentditable: "false"
    }),
    [
      "div",
      {
        class: "language-display",
        style: `visibility: ${showLanguage ? "visible" : "hidden"}`
      },
      getLanguageDisplay(language)
    ],
    codeMirrorSizer,
    gutterDiv
  ];
  return codeBlockDiv;
}
var tokeniseToDomSpec = (text, lang, showLineNumbers = true) => {
  let lineCounter = 1;
  const allLines = [];
  let currentLine = [];
  const tokenCallback = (token, tokenClass) => {
    if (token == "\n") {
      if (currentLine.length == 0) {
        currentLine.push(["span", { class: "cm-text" }, "\n"]);
      }
      allLines.push(
        getDomSpecForLine(currentLine, lineCounter, showLineNumbers)
      );
      lineCounter++;
      currentLine = [];
    } else currentLine.push(["span", { class: `cm-${tokenClass}` }, token]);
  };
  if (typeof CodeMirror?.runMode === "function") {
    CodeMirror?.runMode?.(text, getLanguageMode(lang), tokenCallback);
    if (currentLine.length == 0)
      currentLine.push(["span", { class: "cm-text" }, "\n"]);
    allLines.push(getDomSpecForLine(currentLine, lineCounter, showLineNumbers));
  }
  return allLines;
};
function getDomSpecForLine(line, lineNumber, showLineNumbers = true) {
  return [
    "div",
    { style: "position: relative" },
    ["div", { class: "CodeMirror-linebackground" }],
    [
      "div",
      {
        class: "CodeMirror-gutter-background",
        style: "left: -29px; width: 29px;"
      }
    ],
    // line number
    [
      "div",
      {
        class: "CodeMirror-gutter-wrapper",
        style: `left: -29px; visibility: ${showLineNumbers ? "visible" : "hidden"}`,
        "aria-hidden": "true"
      },
      [
        "div",
        {
          class: "CodeMirror-linenumber",
          style: `left: 0px; width: 9px;`
        },
        `${lineNumber}`
      ]
    ],
    // content of the line
    [
      "pre",
      {
        class: "CodeMirror-line",
        role: "presentation",
        style: "padding: 0 16px; margin: 0;"
      },
      ["span", { role: "presentation" }, ...line]
    ]
  ];
}
var codeMirrorSizerStyleTag = "margin-left: 29px; margin-bottom: 0px; border-right-width: 50px; min-height: 72px; padding-right: 0px; padding-bottom: 0px";

// src/extensions/code-block-server/code-block-server.ts
var CodeBlockServer = Node.create({
  name: "codeBlock",
  marks: "",
  code: true,
  content: "text*",
  addOptions() {
    return {
      HTMLAttributes: {},
      theme: "cadmus-light" /* Light */
    };
  },
  addAttributes() {
    return {
      language: {
        default: "CSS"
      },
      theme: {
        default: "cadmus-light" /* Light */
      },
      lineNumbersVisible: {
        default: true
      },
      languageVisible: {
        default: true
      }
    };
  },
  parseHTML() {
    return [
      {
        tag: "pre",
        preserveWhitespace: "full"
      }
    ];
  },
  renderHTML({ node }) {
    return makeCodeBlockDomSpec(
      node.textContent,
      node.attrs.language,
      node.attrs.theme,
      node.attrs.lineNumbersVisible,
      node.attrs.languageVisible
    );
  }
});

// src/extensions/cadmus-kit/cadmus-kit-server.ts
var CadmusKitServer = Extension.create({
  name: "cadmusKit",
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          attributes: () => {
            return {
              role: "textbox",
              "aria-multiline": "true",
              translate: "no",
              id: `cadmus-${this.options.editorId}`
            };
          }
        }
      })
    ];
  },
  addExtensions() {
    const {
      enableBlanks = false,
      enableImage = true,
      enableAudio = true,
      enableVideo = true,
      enableHyperlink = true
    } = this.options;
    const extensions = [
      Document,
      Heading.configure({
        levels: [1, 2, 3, 4]
      }),
      HardBreak,
      Paragraph,
      Blockquote,
      Bold,
      Italic,
      Strike,
      Text,
      MathServerExtension,
      Table,
      List,
      Emoji,
      EmptyMediaNode.configure({
        nodeNames: [
          ...enableImage ? ["image"] : [],
          ...enableAudio ? ["audio"] : [],
          ...enableVideo ? ["video"] : []
        ]
      }),
      ListItem,
      BulletList,
      OrderedList,
      ...enableHyperlink ? [Hyperlink] : [],
      this.options.enableHangingIndent ? Indentation.configure({
        specialTypes: ["paragraph"],
        levelTypes: [],
        indentationStyles: ["hanging"],
        defaultIndentation: "hanging"
      }) : Indentation,
      FootnoteStatic,
      Thread,
      Superscript,
      Subscript,
      TextAlign.configure({
        types: ["paragraph", "heading", "image"]
      }),
      Underline,
      Highlight,
      TextStyle,
      TextColor,
      TaskItem,
      TaskList,
      ...enableBlanks ? [Blank] : [],
      PasteChip,
      ...enableImage ? [Image] : [],
      ...enableAudio ? [Audio] : [],
      ...enableVideo ? [Video] : [],
      TrailingNode,
      BlockTextVisibility.configure({
        types: ["tableCaption", "tableFootnote"]
      }),
      BlockBackground.configure({
        types: ["table", "tableCell", "tableHeader"]
      }),
      CodeBlockServer,
      Code,
      Collab.configure({
        version: this.options.version,
        clientId: this.options.clientId
      }),
      Telemetry.configure({
        editorId: this.options.editorId
      })
    ];
    return extensions;
  }
});

// src/server.ts
import { EditorState } from "@tiptap/pm/state";
import { Editor, getSchema, createDocument } from "@tiptap/core";
import {
  sendableSteps,
  getVersion,
  receiveTransaction,
  collab
} from "@tiptap/pm/collab";
import { Step } from "@tiptap/pm/transform";
import { generateHTML } from "@tiptap/html";
export {
  CadmusEditorWrapper,
  CadmusKitServer,
  Editor,
  EditorState,
  Step,
  collab,
  textWordCount as countWords,
  createDocument,
  editorWordCount,
  generateHTML,
  getSchema,
  getText,
  getVersion,
  listFootnotes,
  receiveTransaction,
  sendableSteps,
  textWordCount
};
//# sourceMappingURL=server.mjs.map